import axios from "axios";

const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || "development";
console.log("Current ENVIRONMENT:", ENVIRONMENT);
let BACKEND_URL = "";

if (ENVIRONMENT === "production") {
  // Production-specific configuration
  console.log("Running in production mode");
  BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "/";
} else {
  console.log("Running in development mode");
  BACKEND_URL = process.env.REACT_APP_BACKEND_URL_FOR_BOLT || "http://localhost:8001";
}

console.log("Using BACKEND_URL:", BACKEND_URL);

// Configure axios
const api = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export { api };

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Check if this is a public endpoint that doesn't require authentication
      const url = error.config?.url || "";
      const publicEndpoints = [
        "/api/posts",
        "/api/users/",
        "/api/auth/login",
        "/api/auth/register",
        "/api/auth/forgot-password",
        "/api/auth/login-supabase",
        "/api/auth/register-supabase",
        "/api/auth/supabase",
        "/api/health",
        "/api/contact",
      ];

      // Don't redirect to login for public endpoints
      const isPublicEndpoint = publicEndpoints.some(
        (endpoint) =>
          url.includes(endpoint) &&
          (url === endpoint || url.startsWith(endpoint + "/") || url.startsWith(endpoint + "?"))
      );

      if (!isPublicEndpoint) {
        localStorage.removeItem("token");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Types
export interface User {
  id: string;
  username: string;
  email: string;
  bio?: string;
  avatar?: string;
  mojo: number;
  followers_count: number;
  following_count: number;
  posts_count: number;
  level?: string; // "user", "admin", "banned"
  ban_until?: string;
  ban_permanent?: boolean;
  ban_reason?: string;
  notifications?: Notification[];
  last_login?: string;
  created_at: string;
}

export interface Post {
  id: string;
  title: string;
  content: string;
  hashtags: string[];
  image?: string;
  author_id: string;
  author_username: string;
  author_avatar?: string; // opcional, por si no todos los posts tienen avatar
  visits: number;
  average_rating: number;
  ratings_count: number;
  comments_count: number;
  archived: boolean;
  created_at: string;
  updated_at: string;
  language: string;
}

export interface Comment {
  id: string;
  post_id: string;
  author_id: string;
  author_username: string;
  author_avatar?: string;
  content: string;
  created_at: string;
  updated_at?: string; // Optional field for edited comments
}

export interface Rating {
  id: string;
  post_id: string;
  user_id: string;
  rating: number;
  created_at: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  bio?: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
}

export interface Author {
  id: string;
  username: string;
  bio: string;
  avatar?: string;
  mojo: number;
  posts_count: number;
  followers_count: number;
  average_rating: number;
  total_visits: number;
  ratings_count: number;
  created_at: string;
}

export interface FollowUser {
  id: string;
  username: string;
  bio: string;
  avatar?: string;
  mojo: number;
  followers_count: number;
  following_count: number;
  created_at: string;
}

export interface FollowersResponse {
  followers: FollowUser[];
  total: number;
  skip: number;
  limit: number;
}

export interface FollowingResponse {
  following: FollowUser[];
  total: number;
  skip: number;
  limit: number;
}

export interface AdminUser {
  id: string;
  username: string;
  email: string;
  level: string;
  ban_until?: string;
  ban_permanent: boolean;
  ban_reason?: string;
  posts_count: number;
  followers_count: number;
  mojo: number;
  created_at: string;
  last_login?: string;
}

export interface BannedUser {
  id: string;
  username: string;
  email: string;
  ban_permanent: boolean;
  ban_until?: string;
  ban_reason?: string;
  created_by_admin?: string;
  time_remaining: string;
  created_at: string;
}

export interface UserNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  created_at: string;
  created_by: string;
  read?: boolean;
  post_title?: string;
}

export interface AdminUsersResponse {
  users: AdminUser[];
  total: number;
  skip: number;
  limit: number;
}

export interface AdminStatsResponse {
  total_users: number;
  active_posts: number;
  banned_users: number;
  total_posts: number;
}

export interface BannedUsersResponse {
  banned_users: BannedUser[];
  total: number;
  skip: number;
  limit: number;
}

export interface NotificationsResponse {
  notifications: UserNotification[];
  total: number;
  unread_count: number;
  skip: number;
  limit: number;
}

export interface AuthorsResponse {
  authors: Author[];
  total: number;
  skip: number;
  limit: number;
}

export interface CreatePostRequest {
  title: string;
  content: string;
  hashtags: string[];
  image?: File;
  language: string;
}

export interface TheEditionResponse {
  new_articles_count: number;
  posts: Post[];
  message?: string;
}

export interface FrontpageAggregateResponse {
  hero: Post | null;
  featured: Post[];
  recent: Post[];
  ranks: Post[];
  hashtags: { hashtag: string; count: number }[];
}

// Auth Services
export const authService = {
  login: async (data: LoginRequest): Promise<TokenResponse> => {
    const response = await api.post("/api/auth/login-supabase", data);
    return response.data;
  },

  register: async (data: RegisterRequest): Promise<any> => {
    const response = await api.post("/api/auth/register-supabase", {
      email: data.email,
      password: data.password,
    });
    return response.data;
  },

  exchangeSupabaseToken: async (access_token: string): Promise<TokenResponse> => {
    const response = await api.post("/api/auth/supabase", { access_token });
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get("/api/users/me");
    return response.data;
  },
};

// User Services
export const userService = {
  getProfile: async (username: string): Promise<User> => {
    const response = await api.get(`/api/users/${username}`);
    return response.data;
  },

  isFollowing: async (username: string): Promise<{ is_following: boolean }> => {
    const response = await api.get(`/api/users/${username}/is-following`);
    return response.data;
  },

  updateProfile: async (profileData: { username?: string; bio?: string; avatar?: File }): Promise<User> => {
    const formData = new FormData();

    if (profileData.username !== undefined) {
      formData.append("username", profileData.username);
    }
    if (profileData.bio !== undefined) {
      formData.append("bio", profileData.bio);
    }
    if (profileData.avatar) {
      formData.append("avatar", profileData.avatar);
    }

    const response = await api.put("/api/users/me", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  followUser: async (username: string): Promise<{ message: string }> => {
    const response = await api.post(`/api/follow/${username}`);
    return response.data;
  },
};

// Post Services
export const postService = {
  getPosts: async (
    skip = 0,
    limit = 20,
    hashtag?: string,
    languages?: string[],
    authorUsername?: string
  ): Promise<Post[]> => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });
    if (hashtag) {
      params.append("hashtag", hashtag);
    }

    if (languages && languages.length > 0) {
      // Agregar múltiples valores languages=es&languages=en
      languages.forEach((lang) => params.append("languages", lang));
    }
    if (authorUsername) {
      params.append("author_username", authorUsername);
    }

    const response = await api.get(`/api/posts?${params}`);
    return response.data;
  },

  getPost: async (id: string): Promise<Post> => {
    const response = await api.get(`/api/posts/${id}`);
    return response.data;
  },

  createPost: async (data: CreatePostRequest): Promise<Post> => {
    const formData = new FormData();
    formData.append("title", data.title);
    formData.append("content", data.content);
    formData.append("hashtags", JSON.stringify(data.hashtags));
    formData.append("language", data.language);

    if (data.language) {
      formData.append("language", data.language);
    }

    if (data.image) {
      formData.append("image", data.image);
    }

    const response = await api.post("/api/posts", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  saveDraft: async (data: CreatePostRequest): Promise<Post> => {
    const formData = new FormData();
    formData.append("title", data.title);
    formData.append("content", data.content);
    formData.append("hashtags", JSON.stringify(data.hashtags));
    formData.append("language", data.language);

    if (data.image) {
      formData.append("image", data.image);
    }

    const response = await api.post("/api/posts/draft", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  updatePost: async (postId: string, data: CreatePostRequest): Promise<Post> => {
    const formData = new FormData();
    formData.append("title", data.title);
    formData.append("content", data.content);
    formData.append("hashtags", JSON.stringify(data.hashtags));

    if (data.language) {
      formData.append("language", data.language);
    }

    if (data.image) {
      formData.append("image", data.image);
    }
    // Support removing image explicitly
    // @ts-ignore - allow extra flag alongside CreatePostRequest for update
    if ((data as any).remove_image) {
      formData.append("remove_image", "true");
    }

    const response = await api.put(`/api/posts/${postId}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  deletePost: async (postId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/api/posts/${postId}`);
    return response.data;
  },

  getHeroPost: async (): Promise<Post> => {
    const response = await api.get<Post>("/api/posts/hero");
    return response.data;
  },

  ratePost: async (postId: string, rating: number): Promise<Rating> => {
    const response = await api.post(`/api/posts/${postId}/rate`, {
      post_id: postId,
      rating,
    });
    return response.data;
  },

  getFrontpage: async (languages?: string[]): Promise<Post[]> => {
    const params = new URLSearchParams();
    if (languages && languages.length > 0) {
      languages.forEach((lang) => params.append("languages", lang));
    }

    const response = await api.get(`/api/frontpage?${params}`);
    return response.data;
  },

  getFrontpageAggregate: async (languages?: string[]): Promise<FrontpageAggregateResponse> => {
    const params = new URLSearchParams();
    if (languages && languages.length > 0) {
      languages.forEach((lang) => params.append("languages", lang));
    }
    const qs = params.toString();
    const response = await api.get(`/api/frontpage/aggregate${qs ? `?${qs}` : ""}`);
    return response.data;
  },

  getRanks: async (
    hashtag?: string,
    timePeriod?: string,
    criteria?: string,
    languages?: string[]
  ): Promise<{ posts: Post[] }> => {
    // console.log("🔍 API SERVICE DEBUG - getRanks called with:");
    // console.log("   hashtag:", hashtag);
    // console.log("   timePeriod:", timePeriod);
    // console.log("   criteria:", criteria);

    const params = new URLSearchParams();
    if (hashtag) params.append("hashtag", hashtag);
    if (timePeriod) params.append("time_period", timePeriod);
    if (criteria) params.append("criteria", criteria);
    if (languages && languages.length > 0) {
      languages.forEach((lang) => params.append("languages", lang));
    }

    const queryString = params.toString();
    const finalUrl = `/api/ranks${queryString ? `?${queryString}` : ""}`;

    console.log("📡 Making request to:", finalUrl);
    console.log("🔗 Full URL will be:", `${BACKEND_URL}${finalUrl}`);

    const response = await api.get(finalUrl);

    console.log("📥 API Response status:", response.status);
    console.log("📊 Posts received:", response.data.posts?.length || 0);

    return response.data;
  },

  getPublicPostsByUsername: async (username: string, skip = 0, limit = 20): Promise<Post[]> => {
    const params = new URLSearchParams({ skip: skip.toString(), limit: limit.toString() });
    const response = await api.get(`/api/users/${username}/posts-public?${params}`);
    return response.data;
  },

  getUserPosts: async (
    userId: string,
    skip = 0,
    limit = 100,
    sortBy?: string,
    search?: string,
    archived?: boolean
  ): Promise<Post[]> => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });

    if (sortBy) {
      params.append("sort_by", sortBy);
    }
    if (search) {
      params.append("search", search);
    }
    if (archived !== undefined) {
      params.append("archived", archived.toString());
    }

    const response = await api.get(`/api/users/${userId}/posts?${params}`);
    return response.data;
  },

  getUserPostsSummary: async (
    userId: string
  ): Promise<{ totalViews: number; totalRatings: number; totalComments: number; averageRating: number }> => {
    const response = await api.get(`/api/users/${userId}/posts-summary`);
    return response.data;
  },

  toggleArchivePost: async (postId: string): Promise<{ message: string; archived: boolean }> => {
    const response = await api.put(`/api/posts/${postId}/archive`);
    return response.data;
  },
};

// Comment Services
export const commentService = {
  getComments: async (postId: string): Promise<Comment[]> => {
    const response = await api.get(`/api/posts/${postId}/comments`);
    return response.data;
  },

  createComment: async (postId: string, content: string): Promise<Comment> => {
    const response = await api.post(`/api/posts/${postId}/comments`, {
      content,
    });
    return response.data;
  },

  updateComment: async (commentId: string, content: string): Promise<Comment> => {
    const response = await api.put(`/api/comments/${commentId}`, {
      content,
    });
    return response.data;
  },

  deleteComment: async (commentId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/api/comments/${commentId}`);
    return response.data;
  },
};

// Hashtag Services
export const hashtagService = {
  getPopularHashtags: async (): Promise<{ hashtag: string; count: number }[]> => {
    const response = await api.get("/api/hashtags");
    return response.data;
  },
};

// Author Services
export const authorService = {
  getAuthors: async (skip = 0, limit = 20, sortBy?: string, search?: string): Promise<AuthorsResponse> => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });

    if (sortBy) {
      params.append("sort_by", sortBy);
    }
    if (search) {
      params.append("search", search);
    }

    const response = await api.get(`/api/authors?${params}`);
    return response.data;
  },
};

// Follow Services
export const followService = {
  followUser: async (username: string): Promise<{ message: string }> => {
    const response = await api.post(`/api/follow/${username}`);
    return response.data;
  },

  unfollowUser: async (userId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/api/follow/${userId}`);
    return response.data;
  },

  getFollowers: async (username: string, skip = 0, limit = 50): Promise<FollowersResponse> => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });

    const response = await api.get(`/api/users/${username}/followers?${params}`);
    return response.data;
  },

  getFollowing: async (username: string, skip = 0, limit = 50): Promise<FollowingResponse> => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });

    const response = await api.get(`/api/users/${username}/following?${params}`);
    return response.data;
  },
};

// The Edition Services

export const theEditionService = {
  getEdition: async (languages?: string[]): Promise<TheEditionResponse> => {
    const params = new URLSearchParams();
    if (languages && languages.length > 0) {
      languages.forEach((lang) => params.append("languages", lang));
    }
    const qs = params.toString();
    const response = await api.get(`/api/users/the-edition${qs ? `?${qs}` : ""}`);
    return response.data;
  },
};

// Admin Services
export const adminService = {
  getStats: async (): Promise<AdminStatsResponse> => {
    const response = await api.get("/api/admin/stats");
    return response.data;
  },

  getUsers: async (skip = 0, limit = 50, level?: string, search?: string): Promise<AdminUsersResponse> => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });

    if (level) params.append("level", level);
    if (search) params.append("search", search);

    const response = await api.get(`/api/admin/users?${params}`);
    return response.data;
  },

  banUser: async (data: {
    user_id: string;
    ban_type: "temporary" | "permanent";
    ban_reason: string;
    ban_until?: string;
  }): Promise<{ message: string }> => {
    const response = await api.post("/api/admin/ban-user", data);
    return response.data;
  },

  unbanUser: async (userId: string): Promise<{ message: string }> => {
    const response = await api.post(`/api/admin/unban-user/${userId}`);
    return response.data;
  },

  updateUserLevel: async (data: { user_id: string; level: "user" | "admin" }): Promise<{ message: string }> => {
    const response = await api.post("/api/admin/update-user-level", data);
    return response.data;
  },

  deletePost: async (postId: string, reason: string): Promise<{ message: string }> => {
    const params = new URLSearchParams({ reason });
    const response = await api.delete(`/api/admin/delete-post/${postId}?${params}`);
    return response.data;
  },

  sendNotification: async (data: {
    user_id: string;
    subject: string;
    message: string;
    notification_type: string;
  }): Promise<{ message: string }> => {
    const response = await api.post("/api/admin/send-notification", data);
    return response.data;
  },

  sendBroadcast: async (data: {
    subject: string;
    message: string;
    notification_type?: string;
    target_levels?: ("user" | "admin")[];
    include_banned?: boolean;
  }): Promise<{ message: string; recipients: number }> => {
    const response = await api.post("/api/admin/broadcast-notification", {
      subject: data.subject,
      message: data.message,
      notification_type: data.notification_type || "general",
      target_levels: data.target_levels,
      include_banned: data.include_banned || false,
    });
    return response.data;
  },

  getBannedUsers: async (skip = 0, limit = 50): Promise<BannedUsersResponse> => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });

    const response = await api.get(`/api/admin/banned-users?${params}`);
    return response.data;
  },
  cleanupWeekly: async (): Promise<{ message: string; deleted: number }> => {
    const response = await api.post("/api/admin/notifications/cleanup-weekly");
    return response.data;
  },
  cleanupRatings: async (): Promise<{ message: string; deleted: number }> => {
    const response = await api.post("/api/admin/notifications/cleanup-ratings");
    return response.data;
  },
};

export const notificationService = {
  getNotifications: async (skip = 0, limit = 20, unreadOnly = false): Promise<NotificationsResponse> => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      unread_only: unreadOnly.toString(),
    });

    const response = await api.get(`/api/notifications?${params}`);
    return response.data;
  },

  markAsRead: async (notificationId: string): Promise<{ message: string }> => {
    const response = await api.post(`/api/notifications/${notificationId}/mark-read`);
    return response.data;
  },

  // ✅ Nuevo método
  markAllAsRead: async (): Promise<{ message: string }> => {
    const response = await api.post(`/api/notifications/mark-all-read`);
    return response.data;
  },
  clearAll: async (): Promise<{ message: string }> => {
    const response = await api.delete("/api/notifications/clear");
    return response.data;
  },

  deleteNotification: async (notificationId: string): Promise<void> => {
    await api.delete(`/api/notifications/${notificationId}`);
  },
};

export const contactService = {
  sendMessage: async (data: { email?: string; subject?: string; message: string }): Promise<{ message: string }> => {
    const response = await api.post("/api/contact", data);
    return response.data;
  },
  listMessages: async (
    skip = 0,
    limit = 50
  ): Promise<{ messages: any[]; total: number; skip: number; limit: number }> => {
    const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
    const response = await api.get(`/api/admin/contact-messages?${params}`);
    return response.data;
  },
  replyMessage: async (id: string, reply: string): Promise<{ message: string }> => {
    const response = await api.post(`/api/admin/contact-messages/${id}/reply`, { reply });
    return response.data;
  },
  deleteMessage: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/api/admin/contact-messages/${id}`);
    return response.data;
  },
};

export default api;
